import { useMemo, useState } from 'react';
import { AlertTriangle, BookOpen, Building2, Check, ChevronDown, Download, FileImage, FileText, Info, Landmark, Network, Pencil, Save, ShieldCheck, Users, X, Zap } from 'lucide-react';

type RaciCode = '' | 'A' | 'R' | 'C' | 'I';
type DecisionLevel = 'Strategique' | 'Tactique' | 'Operationnel';
type StakeholderType = 'Client' | 'BZ' | 'Fournisseur' | 'Support générique';

type FunctionRole = {
  id: string;
  name: string;
  category: 'Client' | 'BZ' | 'Fournisseurs';
  description: string;
};

type Stakeholder = {
  id: string;
  functionId: string;
  name: string;
  organization: string;
  role: string;
  email: string;
  phone: string;
  type: StakeholderType;
  accessStatus: 'Actif' | 'À activer' | 'Non requis' | 'Suspendu' | 'À valider';
};

type Pillar = {
  id: string;
  short: string;
  title: string;
  description: string;
  color: string;
};

type Activity = {
  id: string;
  title: string;
  description: string;
  pillarId: string;
  level: DecisionLevel;
  frequency: string;
  evidence: string;
  nistFunction?: 'Govern' | 'Identify' | 'Protect' | 'Detect' | 'Respond' | 'Recover';
  raci: Record<string, RaciCode>;
};

type TabId = 'overview' | 'matrix' | 'team' | 'p1' | 'p2' | 'p3' | 'p4' | 'p5' | 'p6' | 'org' | 'guide';

type State = {
  functions: FunctionRole[];
  stakeholders: Stakeholder[];
  activities: Activity[];
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

const STORAGE_KEY = 'prisme-gouvernance-ti-ebauche-v1';
const raciCycle: RaciCode[] = ['', 'A', 'R', 'C', 'I'];

const pillars: Pillar[] = [
  { id: 'p1', short: '01', title: 'Gouvernance, stratégie & organisation', description: 'Cap, rôles, décisions, comités et priorités.', color: 'blue' },
  { id: 'p2', short: '02', title: 'Architecture, infrastructure & cloud', description: 'Fondations technologiques, résilience et capacité.', color: 'cyan' },
  { id: 'p3', short: '03', title: 'Applications, données & intégrations', description: 'Patrimoine applicatif, données, flux et ownership.', color: 'violet' },
  { id: 'p4', short: '04', title: 'Services, opérations & expérience utilisateur', description: 'Support, incidents, changements et qualité perçue.', color: 'emerald' },
  { id: 'p5', short: '05', title: 'Cybersécurité, risque & conformité', description: 'Protection, risques, contrôles, conformité et résilience.', color: 'amber' },
  { id: 'p6', short: '06', title: 'Transformation, innovation & IA', description: 'Automatisation, IA, modernisation et valeur mesurable.', color: 'fuchsia' },
];

const defaultFunctions: FunctionRole[] = [
  { id: 'client-direction', name: 'Direction générale / président', category: 'Client', description: 'Décisions exécutives, budget, priorités et risques acceptés.' },
  { id: 'client-sponsor', name: 'Sponsor exécutif TI', category: 'Client', description: 'Porteur d’affaires des orientations TI.' },
  { id: 'client-ti', name: 'Responsable TI client', category: 'Client', description: 'Coordination TI côté client.' },
  { id: 'client-ops', name: 'Responsable opérations', category: 'Client', description: 'Impact opérationnel, priorités terrain et adoption.' },
  { id: 'client-finance', name: 'Responsable finances / budget', category: 'Client', description: 'Budget, renouvellements et approbations financières.' },
  { id: 'client-loi25', name: 'Responsable conformité / Loi 25', category: 'Client', description: 'Vie privée, conformité et obligations réglementaires.' },
  { id: 'client-cyber', name: 'Responsable cybersécurité client', category: 'Client', description: 'Risques, posture et coordination de la cybersécurité côté client.' },
  { id: 'client-apps', name: 'Responsable applications métier', category: 'Client', description: 'Ownership des systèmes métiers.' },
  { id: 'client-data', name: 'Propriétaire des données', category: 'Client', description: 'Qualité, flux et usage des données.' },
  { id: 'client-rh', name: 'Responsable RH', category: 'Client', description: 'Adoption, communications humaines et impacts employés.' },
  { id: 'client-communications', name: 'Responsable communications internes', category: 'Client', description: 'Messages aux utilisateurs, gestion du changement et communications internes.' },
  { id: 'bz-dti', name: 'Directeur TI BZ', category: 'BZ', description: 'Gouvernance, recommandations et alignement TI.' },
  { id: 'bz-prime', name: 'Prime BZ', category: 'BZ', description: 'Accompagnement client, coordination et suivi.' },
  { id: 'bz-sac', name: 'SAC BZ', category: 'BZ', description: 'Support, demandes, incidents et escalades.' },
  { id: 'bz-account', name: 'Directeur de compte BZ', category: 'BZ', description: 'Relation client et continuité commerciale.' },
  { id: 'bz-cyber', name: 'Spécialiste cybersécurité BZ', category: 'BZ', description: 'Expertise en cybersécurité, risques, incidents de cybersécurité et conformité.' },
  { id: 'bz-cloud', name: 'Spécialiste cloud / infrastructure BZ', category: 'BZ', description: 'Infrastructure, cloud, résilience, performance.' },
  { id: 'bz-telecom', name: 'Spécialiste télécom BZ', category: 'BZ', description: 'Téléphonie, liens, fournisseurs télécom.' },
  { id: 'bz-apps', name: 'Spécialiste applications / intégrations BZ', category: 'BZ', description: 'Applications, intégrations et flux de données.' },
  { id: 'bz-ia', name: 'Spécialiste IA / automatisation BZ', category: 'BZ', description: 'Automatisation, IA et valeur mesurable.' },
  { id: 'vendor-internet', name: 'Fournisseur Internet', category: 'Fournisseurs', description: 'Support et escalade lien Internet.' },
  { id: 'vendor-telecom', name: 'Fournisseur téléphonie', category: 'Fournisseurs', description: 'Support téléphonie et services connexes.' },
  { id: 'vendor-erp', name: 'Fournisseur ERP / CRM', category: 'Fournisseurs', description: 'Applications métiers structurantes.' },
  { id: 'vendor-software', name: 'Fournisseur logiciel métier', category: 'Fournisseurs', description: 'Support et évolution d’un logiciel métier spécialisé.' },
  { id: 'vendor-cloud', name: 'Fournisseur hébergement / cloud', category: 'Fournisseurs', description: 'Hébergement, services cloud et infrastructure externe.' },
  { id: 'vendor-cyber', name: 'Fournisseur cybersécurité / MDR / SOC', category: 'Fournisseurs', description: 'Détection, réponse et support spécialisé en cybersécurité.' },
  { id: 'vendor-legal', name: 'Conseiller légal / conformité', category: 'Fournisseurs', description: 'Avis légal, conformité et déclarations.' },
  { id: 'vendor-support', name: 'Support générique fournisseur', category: 'Fournisseurs', description: 'Boîte courriel ou numéro de support partagé.' },
];

const defaultStakeholders: Stakeholder[] = defaultFunctions.map((fn) => ({
  id: `st-${fn.id}`,
  functionId: fn.id,
  name: fn.category === 'BZ' ? 'À assigner — BZ' : fn.category === 'Client' ? 'À compléter — client' : 'À compléter — fournisseur',
  organization: fn.category === 'BZ' ? 'BZ inc.' : fn.category === 'Client' ? 'Client' : 'Fournisseur',
  role: fn.name,
  email: '',
  phone: '',
  type: fn.category === 'Fournisseurs' ? 'Fournisseur' : fn.category,
  accessStatus: fn.category === 'Fournisseurs' ? 'Non requis' : 'À valider',
}));

const r = (pairs: Array<[string, RaciCode]>) => Object.fromEntries(pairs) as Record<string, RaciCode>;

const defaultActivities: Activity[] = [
  { id: 'a-vision-ti', title: 'Définir la vision TI annuelle', description: 'Clarifier le cap TI et les priorités d’affaires soutenues par la technologie.', pillarId: 'p1', level: 'Strategique', frequency: 'Annuel', evidence: 'Charte, plan directeur ou compte rendu de décision', raci: r([['client-direction','A'], ['client-sponsor','R'], ['bz-dti','R'], ['bz-prime','C'], ['bz-account','I']]) },
  { id: 'a-budget', title: 'Approuver les investissements TI majeurs', description: 'Trancher les budgets structurants selon valeur, risque et capacité.', pillarId: 'p1', level: 'Strategique', frequency: 'Annuel / au besoin', evidence: 'Décision budgétaire, ODS, roadmap', raci: r([['client-direction','A'], ['client-finance','C'], ['client-ti','R'], ['bz-dti','R'], ['bz-prime','C'], ['bz-account','I']]) },
  { id: 'a-roadmap', title: 'Revoir la roadmap TI', description: 'Ajuster les priorités trimestrielles et les dépendances.', pillarId: 'p1', level: 'Tactique', frequency: 'Trimestriel', evidence: 'Roadmap mise à jour', raci: r([['client-sponsor','A'], ['client-ti','R'], ['bz-prime','R'], ['bz-dti','C'], ['bz-account','I']]) },
  { id: 'a-pv', title: 'Documenter les décisions du comité', description: 'Conserver la trace des décisions, raisons, actions et suivis.', pillarId: 'p1', level: 'Operationnel', frequency: 'Chaque comité', evidence: 'Compte rendu et registre de décisions', raci: r([['bz-prime','R'], ['client-sponsor','A'], ['bz-account','I']]) },
  { id: 'a-architecture', title: 'Valider l’architecture cible', description: 'Aligner architecture, cloud, résilience et croissance.', pillarId: 'p2', level: 'Strategique', frequency: 'Annuel', evidence: 'Schéma cible ou orientation approuvée', raci: r([['client-ti','A'], ['bz-cloud','R'], ['bz-dti','C'], ['client-direction','I']]) },
  { id: 'a-renouvellements', title: 'Suivre les renouvellements majeurs', description: 'Anticiper licences, matériel, contrats et capacité.', pillarId: 'p2', level: 'Tactique', frequency: 'Trimestriel', evidence: 'Liste de renouvellements et décisions', raci: r([['client-finance','A'], ['bz-prime','R'], ['bz-cloud','C'], ['bz-account','C']]) },
  { id: 'a-infra-incidents', title: 'Escalader un incident infrastructure', description: 'Coordonner incident majeur, fournisseur et communication.', pillarId: 'p2', level: 'Operationnel', frequency: 'Au besoin', evidence: 'Ticket, suivi, communication', raci: r([['client-ti','A'], ['bz-sac','R'], ['bz-cloud','R'], ['vendor-internet','C'], ['client-ops','I']]) },
  { id: 'a-app-owners', title: 'Nommer les propriétaires applicatifs', description: 'Clarifier qui décide pour chaque application métier critique.', pillarId: 'p3', level: 'Strategique', frequency: 'Annuel', evidence: 'Registre des applications et propriétaires', raci: r([['client-sponsor','A'], ['client-apps','R'], ['client-data','C'], ['bz-apps','C']]) },
  { id: 'a-integrations', title: 'Prioriser les intégrations', description: 'Choisir les flux et automatisations qui créent le plus de valeur.', pillarId: 'p3', level: 'Tactique', frequency: 'Trimestriel', evidence: 'Backlog intégrations priorisé', raci: r([['client-apps','A'], ['bz-apps','R'], ['client-data','C'], ['bz-ia','C']]) },
  { id: 'a-app-incident', title: 'Suivre les incidents applicatifs', description: 'Gérer les problèmes applicatifs récurrents et fournisseurs.', pillarId: 'p3', level: 'Operationnel', frequency: 'Mensuel / au besoin', evidence: 'Liste incidents et actions', raci: r([['client-apps','A'], ['bz-sac','R'], ['vendor-erp','C'], ['client-ops','I']]) },
  { id: 'a-sla', title: 'Valider les attentes de service', description: 'Définir la qualité attendue du support, des communications et des suivis.', pillarId: 'p4', level: 'Strategique', frequency: 'Annuel', evidence: 'Charte de service ou attentes confirmées', raci: r([['client-direction','A'], ['client-ops','R'], ['bz-dti','C'], ['bz-sac','C']]) },
  { id: 'a-kpi-support', title: 'Revoir les indicateurs de support', description: 'Suivre incidents, délais, satisfaction et irritants.', pillarId: 'p4', level: 'Tactique', frequency: 'Trimestriel', evidence: 'Rapport support / comité TI', raci: r([['client-ti','A'], ['bz-sac','R'], ['bz-prime','C'], ['client-ops','I']]) },
  { id: 'a-incident-major', title: 'Gérer un incident majeur', description: 'Identifier, escalader, communiquer et fermer l’incident.', pillarId: 'p4', level: 'Operationnel', frequency: 'Au besoin', evidence: 'Ticket, post-mortem, communication', raci: r([['client-ti','A'], ['bz-sac','R'], ['bz-prime','C'], ['client-direction','I'], ['bz-account','I']]) },
  { id: 'a-cyber-gov', title: 'Définir la gouvernance de cybersécurité', description: 'Établir rôles, politiques, risques de cybersécurité et supervision de la cybersécurité.', pillarId: 'p5', level: 'Strategique', frequency: 'Annuel', evidence: 'Charte de cybersécurité, registre des risques de cybersécurité', nistFunction: 'Govern', raci: r([['client-direction','A'], ['client-cyber','R'], ['bz-cyber','R'], ['client-loi25','C'], ['bz-dti','C']]) },
  { id: 'a-cyber-identify', title: 'Identifier les actifs et données sensibles', description: 'Comprendre ce qui doit être protégé et les impacts.', pillarId: 'p5', level: 'Tactique', frequency: 'Trimestriel', evidence: 'Liste actifs/données critiques', nistFunction: 'Identify', raci: r([['client-cyber','A'], ['client-data','R'], ['bz-cyber','C'], ['client-apps','C']]) },
  { id: 'a-cyber-protect', title: 'Valider MFA, accès et sauvegardes', description: 'Confirmer les protections de base et les priorités de renforcement.', pillarId: 'p5', level: 'Tactique', frequency: 'Trimestriel', evidence: 'Rapport posture / preuves', nistFunction: 'Protect', raci: r([['client-cyber','A'], ['bz-cyber','R'], ['client-ti','C'], ['bz-cloud','C']]) },
  { id: 'a-cyber-detect', title: 'Suivre les alertes de sécurité', description: 'Définir qui surveille, qualifie et escalade les alertes.', pillarId: 'p5', level: 'Operationnel', frequency: 'Mensuel / au besoin', evidence: 'Alertes, tickets, journal de suivi', nistFunction: 'Detect', raci: r([['client-cyber','A'], ['vendor-cyber','R'], ['bz-cyber','R'], ['bz-sac','I']]) },
  { id: 'a-cyber-respond', title: 'Gérer un incident de cybersécurité', description: 'Déclencher la procédure, joindre les responsables et documenter.', pillarId: 'p5', level: 'Operationnel', frequency: 'Au besoin', evidence: 'Registre d’incident de cybersécurité, décisions, post-mortem', nistFunction: 'Respond', raci: r([['client-direction','A'], ['bz-cyber','R'], ['client-cyber','R'], ['client-loi25','C'], ['vendor-legal','C'], ['bz-account','I']]) },
  { id: 'a-cyber-recover', title: 'Valider le retour à la normale', description: 'Confirmer la reprise, les leçons apprises et les contrôles à ajuster.', pillarId: 'p5', level: 'Operationnel', frequency: 'Après incident', evidence: 'Rapport post-incident', nistFunction: 'Recover', raci: r([['client-ti','A'], ['bz-cloud','R'], ['bz-cyber','C'], ['client-direction','I']]) },
  { id: 'a-ia-vision', title: 'Définir la vision innovation / IA', description: 'Encadrer l’innovation, l’automatisation et les risques liés aux données.', pillarId: 'p6', level: 'Strategique', frequency: 'Annuel', evidence: 'Orientation innovation / IA', raci: r([['client-direction','A'], ['client-sponsor','R'], ['bz-ia','C'], ['client-data','C'], ['bz-dti','I']]) },
  { id: 'a-automation', title: 'Prioriser les opportunités d’automatisation', description: 'Choisir les cas d’usage réalistes et mesurables.', pillarId: 'p6', level: 'Tactique', frequency: 'Trimestriel', evidence: 'Backlog automatisation / POC', raci: r([['client-ops','A'], ['bz-ia','R'], ['client-apps','C'], ['bz-apps','C']]) },
  { id: 'a-adoption', title: 'Former les utilisateurs aux nouveaux usages', description: 'Accompagner l’adoption et recueillir les irritants.', pillarId: 'p6', level: 'Operationnel', frequency: 'Au besoin', evidence: 'Plan de formation / adoption', raci: r([['client-ops','A'], ['bz-ia','R'], ['client-rh','C'], ['client-communications','C']]) },
];

const defaultState: State = { functions: defaultFunctions, stakeholders: defaultStakeholders, activities: defaultActivities };

function loadInitialState(): State {
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (!saved) return defaultState;
    const parsed = JSON.parse(saved) as Partial<State>;
    return {
      functions: parsed.functions?.length ? parsed.functions : defaultFunctions,
      stakeholders: parsed.stakeholders?.length ? parsed.stakeholders : defaultStakeholders,
      activities: parsed.activities?.length ? parsed.activities : defaultActivities,
    };
  } catch {
    return defaultState;
  }
}

const tabLabels: Array<{ id: TabId; label: string }> = [
  { id: 'overview', label: 'Vue d’ensemble' },
  { id: 'matrix', label: 'Matrice RACI globale' },
  { id: 'team', label: 'Équipe & rôles' },
  { id: 'p1', label: '01 Gouvernance' },
  { id: 'p2', label: '02 Architecture' },
  { id: 'p3', label: '03 Applications' },
  { id: 'p4', label: '04 Services' },
  { id: 'p5', label: '05 Cybersécurité' },
  { id: 'p6', label: '06 Transformation' },
  { id: 'org', label: 'Organigramme' },
  { id: 'guide', label: 'Guide pédagogique' },
];

const normalizeText = (value: string) => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

export default function GouvernanceTIModal({ isOpen, onClose }: Props) {
  const [state, setState] = useState<State>(loadInitialState);
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [editingTabs, setEditingTabs] = useState<Record<string, boolean>>({});
  const [deliverablesOpen, setDeliverablesOpen] = useState(false);
  const [infoTopic, setInfoTopic] = useState<string | null>(null);
  const [highlightActivityId, setHighlightActivityId] = useState<string | null>(null);
  const [contactFunctionId, setContactFunctionId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [orgFileName, setOrgFileName] = useState<string>('Aucun organigramme importé');

  const saveAll = () => {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    setEditingTabs({});
  };

  const isEditing = Boolean(editingTabs[activeTab]);
  const setEditing = (value: boolean) => setEditingTabs((prev) => ({ ...prev, [activeTab]: value }));

  const visibleFunctions = state.functions;

  const matrixActivities = useMemo(() => {
    const q = normalizeText(search);
    return state.activities.filter((activity) => !q || normalizeText(`${activity.title} ${activity.description}`).includes(q));
  }, [state.activities, search]);

  if (!isOpen) return null;

  const updateActivity = (id: string, patch: Partial<Activity>) => {
    setState((prev) => ({ ...prev, activities: prev.activities.map((a) => a.id === id ? { ...a, ...patch } : a) }));
  };

  const updateFunction = (id: string, patch: Partial<FunctionRole>) => {
    setState((prev) => ({ ...prev, functions: prev.functions.map((fn) => fn.id === id ? { ...fn, ...patch } : fn) }));
  };

  const updateStakeholder = (id: string, patch: Partial<Stakeholder>) => {
    setState((prev) => ({ ...prev, stakeholders: prev.stakeholders.map((st) => st.id === id ? { ...st, ...patch } : st) }));
  };

  const cycleRaci = (activityId: string, functionId: string) => {
    if (!isEditing) return;
    setState((prev) => ({
      ...prev,
      activities: prev.activities.map((activity) => {
        if (activity.id !== activityId) return activity;
        const current = activity.raci[functionId] || '';
        const next = raciCycle[(raciCycle.indexOf(current) + 1) % raciCycle.length];
        return { ...activity, raci: { ...activity.raci, [functionId]: next } };
      }),
    }));
  };

  const goToActivity = (activity: Activity) => {
    setActiveTab(activity.pillarId as TabId);
    setHighlightActivityId(activity.id);
  };

  const getStakeholdersForFunction = (functionId: string) => state.stakeholders.filter((st) => st.functionId === functionId);
  const selectedFunction = contactFunctionId ? state.functions.find((fn) => fn.id === contactFunctionId) : null;
  const selectedStakeholders = contactFunctionId ? getStakeholdersForFunction(contactFunctionId) : [];
  const activeTabLabel = tabLabels.find((tab) => tab.id === activeTab)?.label || 'Vue d’ensemble';

  const validationFor = (activity: Activity) => {
    const codes = Object.values(activity.raci || {});
    const aCount = codes.filter((code) => code === 'A').length;
    const rCount = codes.filter((code) => code === 'R').length;
    const warnings = [] as string[];
    if (aCount === 0) warnings.push('Aucun A');
    if (aCount > 1) warnings.push('Plusieurs A');
    if (rCount === 0) warnings.push('Aucun R');
    return warnings;
  };

  const renderInfoButton = (topic: string) => (
    <button type="button" onClick={() => setInfoTopic(topic)} className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-slate-100 text-slate-500 hover:bg-blue-100 hover:text-blue-700" title="Explication">
      <Info className="h-3.5 w-3.5" />
    </button>
  );

  const renderEditableText = (value: string, onChange: (value: string) => void, className = '') => (
    isEditing ? (
      <input value={value} onChange={(e) => onChange(e.target.value)} className={`w-full rounded-md border border-slate-200 px-2 py-1 text-xs text-slate-800 ${className}`} />
    ) : <span className={className}>{value}</span>
  );

  const renderCell = (activity: Activity, fn: FunctionRole) => {
    const code = activity.raci[fn.id] || '';
    const color = code === 'A' ? 'bg-blue-600 text-white border-blue-600' : code === 'R' ? 'bg-emerald-600 text-white border-emerald-600' : code === 'C' ? 'bg-amber-500 text-white border-amber-500' : code === 'I' ? 'bg-slate-500 text-white border-slate-500' : 'bg-white text-slate-300 border-slate-200';
    return (
      <button
        type="button"
        key={fn.id}
        onClick={() => isEditing ? cycleRaci(activity.id, fn.id) : setContactFunctionId(fn.id)}
        className={`mx-auto flex h-8 w-8 items-center justify-center rounded-md border text-xs font-bold transition ${color} ${isEditing ? 'hover:scale-105' : 'hover:ring-2 hover:ring-blue-200'}`}
        title={isEditing ? 'Cliquer pour changer vide → A → R → C → I' : 'Voir le contact associé'}
      >
        {code || '□'}
      </button>
    );
  };

  const renderMatrix = (activities: Activity[], compact = false) => (
    <div className="max-w-full overflow-x-auto rounded-xl border border-slate-200 bg-white">
      <table className="min-w-[1500px] w-full text-left text-xs">
        <thead className="bg-slate-50 text-slate-600">
          <tr>
            <th className="sticky left-0 z-10 w-72 bg-slate-50 px-3 py-3 font-semibold">Activité</th>
            <th className="w-28 px-3 py-3 font-semibold">Niveau</th>
            <th className="w-44 px-3 py-3 font-semibold">Pilier</th>
            {!compact && <th className="w-32 px-3 py-3 font-semibold">Fréquence</th>}
            {visibleFunctions.map((fn) => <th key={fn.id} className="w-24 px-2 py-3 text-center font-semibold" title={fn.name}>{fn.name.split(' ').slice(0, 2).join(' ')}</th>)}
          </tr>
        </thead>
        <tbody>
          {activities.map((activity) => {
            const pillar = pillars.find((p) => p.id === activity.pillarId);
            const warnings = validationFor(activity);
            const highlighted = highlightActivityId === activity.id;
            return (
              <tr key={activity.id} className={`border-t border-slate-100 ${highlighted ? 'bg-amber-50' : 'hover:bg-slate-50'}`}>
                <td className="sticky left-0 z-10 bg-inherit px-3 py-3 align-top">
                  <button type="button" onClick={() => goToActivity(activity)} className="text-left font-semibold text-slate-900 hover:text-blue-700">
                    {renderEditableText(activity.title, (v) => updateActivity(activity.id, { title: v }))}
                  </button>
                  <p className="mt-1 text-[11px] text-slate-500">{renderEditableText(activity.description, (v) => updateActivity(activity.id, { description: v }))}</p>
                  {warnings.length > 0 && <p className="mt-1 inline-flex items-center gap-1 rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700"><AlertTriangle className="h-3 w-3" /> {warnings.join(' • ')}</p>}
                </td>
                <td className="px-3 py-3 align-top text-slate-600">{activity.level}</td>
                <td className="px-3 py-3 align-top text-slate-600">{pillar?.short} — {pillar?.title}</td>
                {!compact && <td className="px-3 py-3 align-top text-slate-600">{renderEditableText(activity.frequency, (v) => updateActivity(activity.id, { frequency: v }))}</td>}
                {visibleFunctions.map((fn) => <td key={fn.id} className="px-2 py-3 align-top text-center">{renderCell(activity, fn)}</td>)}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  const renderEditBar = () => (
    <div className="flex flex-wrap items-center gap-2">
      {!isEditing ? (
        <button type="button" onClick={() => setEditing(true)} className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-3 py-2 text-sm font-semibold text-white hover:bg-slate-800">
          <Pencil className="h-4 w-4" /> Modification
        </button>
      ) : (
        <>
          <button type="button" onClick={saveAll} className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700">
            <Save className="h-4 w-4" /> Enregistrer
          </button>
          <button type="button" onClick={() => setEditing(false)} className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50">Annuler</button>
        </>
      )}
    </div>
  );

  const renderDeliverablesButton = () => (
    <div className="relative">
      <button type="button" onClick={() => setDeliverablesOpen((v) => !v)} className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700">
        <Download className="h-4 w-4" /> Livrables <ChevronDown className="h-4 w-4" />
      </button>
      {deliverablesOpen && (
        <div className="absolute right-0 z-30 mt-2 w-72 rounded-xl border border-slate-200 bg-white p-2 shadow-xl">
          {['Matrice RACI PDF', 'Rapport gouvernance Comité TI', 'Fiche équipe, rôles & contacts', 'Charte du Comité TI', 'Organigramme de gouvernance'].map((item) => (
            <button key={item} type="button" className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50" onClick={() => setInfoTopic(`Livrable — ${item}`)}>
              <FileText className="h-4 w-4 text-blue-600" /> {item}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  const renderOverview = () => (
    <div className="space-y-6">
      <section className="rounded-2xl bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 p-7 text-white">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-blue-200">Gouvernance TI {renderInfoButton('Gouvernance TI')}</div>
            <h2 className="mt-3 text-3xl font-bold">Comité TI • Matrice RACI • Rôles & responsabilités • Contacts clés • Livrables</h2>
            <p className="mt-3 max-w-3xl text-sm leading-relaxed text-blue-50">Ébauche client-ready pour clarifier qui décide, qui exécute, qui est consulté, qui est informé et qui contacter rapidement pendant un comité TI.</p>
          </div>
          {renderDeliverablesButton()}
        </div>
      </section>

      <section className="grid gap-4 md:grid-cols-4">
        {[
          ['Transparence totale', 'Clarifier qui fait quoi, qui décide, qui est consulté et qui est informé.'],
          ['Autonomie client', 'Le client conserve le pouvoir décisionnel; BZ structure les faits, options et risques.'],
          ['Rythme sur mesure', 'La gouvernance s’adapte aux priorités, au budget et à la réalité du client.'],
          ['Responsabilités actionnables', 'Les fonctions RACI sont liées à des contacts concrets.'],
        ].map(([title, text]) => (
          <div key={title} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <h3 className="font-bold text-slate-900">{title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">{text}</p>
          </div>
        ))}
      </section>

      <section className="grid gap-4 md:grid-cols-5">
        {[
          ['6', 'Piliers BZ client'], ['3', 'Niveaux décisionnels'], ['RACI', 'Méthode standard'], ['NIST 2.0', 'Cybersécurité en 6 fonctions'], ['5', 'Livrables PDF']
        ].map(([number, label]) => (
          <div key={label} className="rounded-xl border border-slate-200 bg-white p-4 text-center shadow-sm">
            <div className="text-2xl font-black text-blue-700">{number}</div>
            <div className="mt-1 text-xs font-semibold text-slate-600">{label}</div>
          </div>
        ))}
      </section>

      <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="mb-4 flex items-center gap-2"><Network className="h-5 w-5 text-blue-700" /><h3 className="text-lg font-bold text-slate-900">Structure de gouvernance</h3>{renderInfoButton('Structure de gouvernance')}</div>
        <div className="grid gap-3 md:grid-cols-4">
          {['Direction / Sponsor exécutif client', 'Comité TI client', 'Responsable TI client / Prime BZ / Directeur TI BZ', 'SAC BZ / Spécialistes BZ / Fournisseurs'].map((node, index) => (
            <div key={node} className="relative rounded-xl border border-blue-100 bg-blue-50 p-4 text-sm font-semibold text-blue-950">
              <span className="mb-2 flex h-7 w-7 items-center justify-center rounded-full bg-blue-600 text-xs text-white">{index + 1}</span>
              {node}
            </div>
          ))}
        </div>
      </section>

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {pillars.map((pillar) => (
          <button key={pillar.id} type="button" onClick={() => setActiveTab(pillar.id as TabId)} className="rounded-xl border border-slate-200 bg-white p-5 text-left shadow-sm transition hover:border-blue-300 hover:shadow-md">
            <div className="text-xs font-bold text-blue-700">{pillar.short}</div>
            <h3 className="mt-1 font-bold text-slate-900">{pillar.title}</h3>
            <p className="mt-2 text-sm text-slate-600">{pillar.description}</p>
          </button>
        ))}
      </section>
    </div>
  );

  const renderTeam = () => (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><h2 className="text-xl font-bold text-slate-900">Équipe & rôles</h2><p className="text-sm text-slate-600">Parties prenantes, fonctions, coordonnées et accès PRIsME client.</p></div>
        {renderEditBar()}
      </div>
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {state.stakeholders.map((st) => {
          const fn = state.functions.find((f) => f.id === st.functionId);
          return (
            <article key={st.id} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex items-start gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-full bg-blue-100 font-bold text-blue-700">{st.name.replace('À compléter — ', '').replace('À assigner — ', '').slice(0, 2).toUpperCase()}</div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{fn?.category} • {st.accessStatus}</p>
                  <h3 className="font-bold text-slate-900">{isEditing ? <input value={st.name} onChange={(e) => updateStakeholder(st.id, { name: e.target.value })} className="w-full rounded border px-2 py-1" /> : st.name}</h3>
                  <p className="text-sm text-slate-600">{fn?.name}</p>
                </div>
              </div>
              <div className="mt-4 space-y-2 text-sm text-slate-600">
                {isEditing ? (
                  <>
                    <input value={st.organization} onChange={(e) => updateStakeholder(st.id, { organization: e.target.value })} placeholder="Organisation" className="w-full rounded-md border px-2 py-1" />
                    <input value={st.role} onChange={(e) => updateStakeholder(st.id, { role: e.target.value })} placeholder="Rôle" className="w-full rounded-md border px-2 py-1" />
                    <input value={st.email} onChange={(e) => updateStakeholder(st.id, { email: e.target.value })} placeholder="Courriel" className="w-full rounded-md border px-2 py-1" />
                    <input value={st.phone} onChange={(e) => updateStakeholder(st.id, { phone: e.target.value })} placeholder="Téléphone" className="w-full rounded-md border px-2 py-1" />
                  </>
                ) : (
                  <>
                    <p><strong>Organisation :</strong> {st.organization}</p>
                    <p><strong>Rôle :</strong> {st.role}</p>
                    <p><strong>Courriel :</strong> {st.email || 'À compléter'}</p>
                    <p><strong>Téléphone :</strong> {st.phone || 'À compléter'}</p>
                  </>
                )}
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );

  const renderPillar = (pillarId: string) => {
    const pillar = pillars.find((p) => p.id === pillarId)!;
    const activities = state.activities.filter((a) => a.pillarId === pillarId);
    const grouped: DecisionLevel[] = ['Strategique', 'Tactique', 'Operationnel'];
    return (
      <div className="space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-sm font-bold text-blue-700">{pillar.short}</p>
            <h2 className="flex items-center gap-2 text-xl font-bold text-slate-900">{pillar.title} {renderInfoButton(pillar.title)}</h2>
            <p className="text-sm text-slate-600">{pillar.description}</p>
          </div>
          {renderEditBar()}
        </div>
        {pillarId === 'p5' && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
            <div className="flex items-center gap-2 font-bold"><ShieldCheck className="h-4 w-4" /> Cybersécurité structurée avec NIST CSF 2.0</div>
            <p className="mt-1">Les activités sont classées par Govern, Identify, Protect, Detect, Respond et Recover pour démarrer simplement.</p>
          </div>
        )}
        {grouped.map((level) => {
          const levelActivities = activities.filter((a) => a.level === level);
          if (!levelActivities.length) return null;
          return (
            <section key={level} className="space-y-3">
              <h3 className="flex items-center gap-2 text-lg font-bold text-slate-900">{level} {renderInfoButton(level)}</h3>
              {pillarId === 'p5' ? (
                ['Govern', 'Identify', 'Protect', 'Detect', 'Respond', 'Recover'].map((nist) => {
                  const nistActivities = levelActivities.filter((a) => a.nistFunction === nist);
                  if (!nistActivities.length) return null;
                  return <div key={nist} className="space-y-2"><h4 className="text-sm font-bold text-amber-800">{nist}</h4>{renderMatrix(nistActivities, true)}</div>;
                })
              ) : renderMatrix(levelActivities, true)}
            </section>
          );
        })}
      </div>
    );
  };

  const renderGuide = () => (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold text-slate-900">Guide pédagogique</h2>
      {[
        ['Comprendre la gouvernance TI', 'La gouvernance TI encadre les décisions technologiques pour soutenir les objectifs d’affaires, réduire les risques et créer de la valeur.'],
        ['Pourquoi un Comité TI?', 'Le Comité TI structure les priorités, projets, risques, budgets, incidents majeurs et décisions technologiques importantes.'],
        ['Comprendre RACI', 'R = réalise ou coordonne. A = approuve et assume. C = consulté avant. I = informé après.'],
        ['Règle d’or', 'Un seul A par activité. Plusieurs A créent de la confusion et ralentissent les décisions.'],
        ['Fonction vs personne', 'La matrice vit par fonction; chaque client associe ensuite une personne, une équipe ou un fournisseur à cette fonction.'],
        ['Modifier la matrice', 'Cliquez sur Modification, changez les textes ou les cellules RACI, puis Enregistrer dans chaque onglet.'],
        ['Pièges à éviter', 'Aucun A, plusieurs A, aucun R, trop de C, contacts incomplets ou fournisseur externe placé comme A d’une décision d’affaires.'],
      ].map(([title, body]) => (
        <section key={title} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="font-bold text-slate-900">{title}</h3>
          <p className="mt-2 text-sm leading-relaxed text-slate-600">{body}</p>
        </section>
      ))}
      <section className="rounded-xl border border-blue-200 bg-blue-50 p-5">
        <h3 className="font-bold text-blue-950">Exemple — incident Internet majeur</h3>
        <p className="mt-2 text-sm text-blue-900">Le Responsable TI client est A, le SAC BZ et le Fournisseur Internet sont R, le Prime BZ et le spécialiste télécom sont C, la Direction client et le Directeur de compte sont I. La bulle contact permet de joindre rapidement Bell ou le fournisseur concerné.</p>
      </section>
    </div>
  );

  const renderOrg = () => (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><h2 className="text-xl font-bold text-slate-900">Organigramme</h2><p className="text-sm text-slate-600">Import PDF/image seulement pour l’ébauche. L’organigramme aide à comprendre; il ne remplace pas le RACI.</p></div>
        {renderEditBar()}
      </div>
      <div className="rounded-2xl border-2 border-dashed border-slate-300 bg-white p-10 text-center">
        <FileImage className="mx-auto h-12 w-12 text-slate-400" />
        <p className="mt-3 font-semibold text-slate-800">{orgFileName}</p>
        <label className="mt-4 inline-flex cursor-pointer items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700">
          Importer PDF/image
          <input type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => setOrgFileName(e.target.files?.[0]?.name || 'Aucun organigramme importé')} />
        </label>
      </div>
    </div>
  );

  const renderContent = () => {
    if (activeTab === 'overview') return renderOverview();
    if (activeTab === 'matrix') return <div className="space-y-5"><div className="flex flex-wrap items-center justify-between gap-3"><div><h2 className="text-xl font-bold text-slate-900">Matrice RACI globale</h2><p className="text-sm text-slate-600">Clique une activité pour ouvrir son pilier. En modification, clique une cellule : vide → A → R → C → I.</p></div><div className="flex items-center gap-2">{renderEditBar()}{renderDeliverablesButton()}</div></div><input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher une activité..." className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />{renderMatrix(matrixActivities)}</div>;
    if (activeTab === 'team') return renderTeam();
    if (activeTab === 'org') return renderOrg();
    if (activeTab === 'guide') return renderGuide();
    return renderPillar(activeTab);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 p-3 sm:p-4">
      <div className="flex h-[92vh] w-[96vw] max-w-[1600px] min-w-0 flex-col overflow-hidden rounded-2xl bg-slate-50 shadow-2xl">
        <header className="shrink-0 border-b border-slate-200 bg-white px-4 py-3 sm:px-6 sm:py-4">
          <div className="flex min-w-0 items-start justify-between gap-4">
            <div className="min-w-0">
              <h1 className="flex min-w-0 flex-wrap items-center gap-2 text-xl font-bold text-slate-950 sm:text-2xl"><Landmark className="h-6 w-6 shrink-0 text-blue-700" /> Gouvernance TI <span className="rounded-full bg-amber-100 px-2 py-1 text-xs font-semibold text-amber-700">Ébauche</span></h1>
              <p className="mt-1 line-clamp-2 text-sm text-slate-600">Structure client PRIsME — 6 piliers, RACI par fonction, parties prenantes, contacts et guide.</p>
            </div>
            <button type="button" onClick={onClose} className="shrink-0 rounded-full p-2 text-slate-500 hover:bg-slate-100"><X className="h-5 w-5" /></button>
          </div>
        </header>

        <nav className="shrink-0 border-b border-slate-200 bg-white px-3 py-2 sm:px-4">
          <div className="flex min-w-0 flex-wrap gap-2">
          {tabLabels.map((tab) => (
            <button key={tab.id} type="button" onClick={() => setActiveTab(tab.id)} className={`rounded-lg px-3 py-2 text-xs font-semibold transition ${activeTab === tab.id ? 'bg-blue-600 text-white shadow-sm ring-2 ring-blue-200' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>{tab.label}</button>
          ))}
          </div>
        </nav>

        <div className="shrink-0 border-b border-slate-200 bg-blue-50 px-4 py-2 text-xs font-semibold text-blue-900 sm:px-6">
          Vous êtes ici : <span className="text-blue-700">{activeTabLabel}</span>
          {activeTab === 'matrix' && <span className="ml-2 text-slate-500">• la matrice défile horizontalement dans son propre cadre; le menu reste visible en haut.</span>}
        </div>

        <main className="min-h-0 min-w-0 flex-1 overflow-y-auto p-4 sm:p-6">{renderContent()}</main>
      </div>

      {infoTopic && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/40 p-4">
          <div className="max-w-lg rounded-2xl bg-white p-6 shadow-2xl">
            <div className="flex items-start justify-between gap-3"><h3 className="text-lg font-bold text-slate-900">{infoTopic}</h3><button onClick={() => setInfoTopic(null)}><X className="h-5 w-5 text-slate-500" /></button></div>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">Explication courte liée au guide pédagogique. Cette ébauche prévoit que chaque icône i ouvre une modale simple et peut rediriger vers la section pertinente du guide.</p>
            <button type="button" onClick={() => { setInfoTopic(null); setActiveTab('guide'); }} className="mt-4 inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-semibold text-white"><BookOpen className="h-4 w-4" /> Voir dans le guide pédagogique</button>
          </div>
        </div>
      )}

      {selectedFunction && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/40 p-4">
          <div className="max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <div className="flex items-start justify-between gap-3"><h3 className="text-lg font-bold text-slate-900">{selectedFunction.name}</h3><button onClick={() => setContactFunctionId(null)}><X className="h-5 w-5 text-slate-500" /></button></div>
            {selectedStakeholders.length ? selectedStakeholders.map((st) => (
              <div key={st.id} className="mt-4 rounded-xl border border-slate-200 p-4 text-sm text-slate-700">
                <p><strong>Nom :</strong> {st.name}</p>
                <p><strong>Rôle :</strong> {st.role}</p>
                <p><strong>Courriel :</strong> {st.email || 'À compléter'}</p>
                <p><strong>Téléphone :</strong> {st.phone || 'À compléter'}</p>
              </div>
            )) : <p className="mt-4 text-sm text-slate-600">Aucune partie prenante associée à cette fonction. Veuillez compléter l’onglet Équipe & rôles.</p>}
            <button type="button" onClick={() => { setContactFunctionId(null); setActiveTab('team'); }} className="mt-4 inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-semibold text-white"><Users className="h-4 w-4" /> Ouvrir Équipe & rôles</button>
          </div>
        </div>
      )}
    </div>
  );
}
